import type { BusEffect, BusEffectKind } from '@busswerks/shared'
import {
  buildDelayFx,
  buildReverbFx,
  buildBitcrushFx,
  buildStutterFx,
  buildMasterFilterFx,
  identityCurve,
  bitcrushCurve,
  type DelayFx,
  type ReverbFx,
  type BitcrushFx,
  type StutterFx,
  type MasterFilterFx,
} from '@/audio/effects'
import { getEffect } from '@/audio/plugins/registry'
import type { PluginHandle } from '@/audio/plugins/types'

/**
 * Mixer bus with volume, pan, and an optional serial FX chain.
 *
 * Signal flow inside a bus with N effects:
 *   input → panner → fx[0] → fx[1] → … → fx[N-1] → volume → output
 *
 * Each FX slot owns a small sub-graph:
 *   - **Parallel-send effects** (delay / reverb / bitcrush / stutter)
 *     run a dry tap in parallel with the effect's wet output; the
 *     bus signal sums dry + wet into the slot's output gain. Wet
 *     level is controlled by the `wet` param; disabled effects
 *     collapse wet to zero so only the dry tap passes through.
 *   - **Serial effects** (filter) replace the dry signal — slot
 *     input flows into the filter, filter output IS the slot
 *     output. "Enabled = false" reconfigures the filter to a
 *     transparent pass-through (lowpass at 22 kHz).
 *
 * The caller connects a bus's `output` to whatever comes next —
 * master bus input for user buses, or the ChannelMergerNode for
 * buses routed to a non-default output pair. Bus class stays
 * unaware of the enclosing topology.
 */

/** Default wet levels per effect kind when a BusEffect is added
 *  without an explicit `wet`. Picked to sound musical on a fresh
 *  add (not zero — that'd make the add feel inert — but not
 *  overpowering either). */
const DEFAULT_WET: Record<BusEffectKind, number> = {
  filter: 1, // filter is serial; wet = intensity of the filter sweep
  delay: 0.45,
  reverb: 0.4,
  bitcrush: 0.7,
  stutter: 0.75,
  // Registry plugins manage their own wet internally via setParam;
  // the bus-level wet still gates via dry/wet mix on the slot.
  plugin: 1,
}

/** Default param values for newly-added effects. Matched to the
 *  master bank's starting values so a fresh per-bus add sounds
 *  similar to an engaged master slot at the same wet level. */
const DEFAULT_PARAMS: Record<BusEffectKind, Record<string, number>> = {
  // `filterMode` encodes the BiquadFilter type: 0 = allpass (bypass),
  // 1 = highpass, 2 = lowpass. Default 2 matches the prior lowpass-
  // only behaviour so existing persisted BusEffect rows keep sounding
  // identical. `Q` is a resonance parameter (0.7 = Butterworth, 10 =
  // very resonant — useful for acid-house sweeps).
  filter: { cutoff: 2000, filterMode: 2, q: 0.707 },
  // `filterMode` is encoded as a number because `BusEffect.params`
  // is `Record<string, number>` in the persisted shape. `0 = off`,
  // `1 = highpass`, `2 = lowpass`. Delay defaults to highpass so
  // kick + bass don't smear into the echoes; stutter defaults off
  // to preserve the full-band snapshot.
  delay: { time: 0.25, feedback: 0.55, filterMode: 1, filterFreq: 200 },
  reverb: {},
  bitcrush: { bits: 4 },
  stutter: { time: 0.08, feedback: 0.95, filterMode: 0, filterFreq: 200 },
  // Plugin kind defers default params to the registered plugin's
  // own `defaultPreset`. Bus.buildSlot merges that preset into the
  // BusEffect's params at slot creation.
  plugin: {},
}

/** Apply the `filterMode` + `filterFreq` params to a delay or
 *  stutter handle's wet-path biquad. Mode is numeric so it fits
 *  `BusEffect.params: Record<string, number>`: 0 = off (allpass,
 *  no coloration), 1 = highpass, 2 = lowpass. */
function applyWetFilter(
  filter: BiquadFilterNode,
  params: Record<string, number>,
  t: number,
): void {
  const mode = params.filterMode
  const freq = params.filterFreq
  if (mode === 0) {
    filter.type = 'allpass'
  } else if (mode === 1) {
    filter.type = 'highpass'
  } else if (mode === 2) {
    filter.type = 'lowpass'
  }
  if (typeof freq === 'number' && freq > 0) {
    filter.frequency.setTargetAtTime(
      Math.max(20, Math.min(20000, freq)),
      t,
      0.01,
    )
  }
}

type AnyFxHandle = DelayFx | ReverbFx | BitcrushFx | StutterFx | MasterFilterFx

/** Per-slot plugin handle for `kind === 'plugin'`. Held separately
 *  from the hardcoded `AnyFxHandle` so buildSlot can return a slot
 *  whose `handle` is a PluginHandle without widening every existing
 *  handle type. */

interface FxSlot {
  id: string
  kind: BusEffectKind
  /** Upstream signal enters here. */
  input: GainNode
  /** Downstream signal exits here. */
  output: GainNode
  /** Dry pass-through (parallel pattern only). `null` for serial
   *  effects like filter where the slot output IS the effect output. */
  dryTap: GainNode | null
  /** Wet gain — the handle's own wet node (for parallel effects)
   *  or a synthesized gain before the filter's output (for serial).
   *  Multiplied by enabled*userWet each apply. */
  wet: GainNode | null
  /** Underlying effect handle so kind-specific param setters can
   *  touch feedback / curve / frequency. Populated for the five
   *  built-in kinds; `null` when this is a registry-plugin slot
   *  (see `pluginHandle`). */
  handle: AnyFxHandle | null
  /** Populated when kind === 'plugin'. Owns the registry-plugin
   *  PluginHandle so applyKindParams can call setParam and
   *  disposeSlot can call dispose. */
  pluginHandle: PluginHandle | null
  /** Plugin id (e.g. `'tone-chorus'`). Saved so reload can find
   *  the same plugin and so disposeSlot logs correctly. */
  pluginId: string | null
  /** Last applied user-facing wet (0..1). Kept separate from
   *  enabled so the UI can retain the fader position across a
   *  bypass toggle. */
  userWet: number
  enabled: boolean
  /** User-provided params — stored so a param-only update doesn't
   *  force a full rebuild of the slot. */
  params: Record<string, number>
}

export class Bus {
  private ctx: AudioContext
  private inputNode: GainNode
  private panner: StereoPannerNode
  private volumeNode: GainNode
  readonly id: string
  private connectedTo: AudioNode | null = null
  /** Head of the FX chain — signal routes panner → chainHead when
   *  `slots` has any entries; otherwise panner connects straight to
   *  volumeNode. Kept as a separate node so the chain can be rebuilt
   *  without re-wiring panner. */
  private chainHead: GainNode
  /** Tail of the FX chain — volume reads from here via volumeNode's
   *  upstream connection. Same rebuild-simplicity reason as
   *  chainHead. */
  private chainTail: GainNode
  /** Current FX slots, in signal-flow order. Empty = dry bus. */
  private slots: FxSlot[] = []

  constructor(ctx: AudioContext, id: string) {
    this.ctx = ctx
    this.id = id
    this.inputNode = ctx.createGain()
    this.inputNode.gain.value = 1
    this.panner = ctx.createStereoPanner()
    this.volumeNode = ctx.createGain()
    this.volumeNode.gain.value = 1
    // Chain head / tail are unity-gain boundary nodes so add/remove
    // effect operations can rewire inside them without touching the
    // volume or panner. With no effects the two are directly
    // connected (dry bus); with effects they anchor the ends of the
    // serial FX chain.
    this.chainHead = ctx.createGain()
    this.chainHead.gain.value = 1
    this.chainTail = ctx.createGain()
    this.chainTail.gain.value = 1
    this.inputNode.connect(this.panner)
    this.panner.connect(this.chainHead)
    this.chainHead.connect(this.chainTail)
    this.chainTail.connect(this.volumeNode)
  }

  /** Voices / upstream buses connect here. Stable reference. */
  get input(): AudioNode {
    return this.inputNode
  }

  /**
   * Connect this bus's output to the given node. Idempotent — calling
   * more than once with the same target is a no-op; calling with a
   * new target rewires cleanly. Safe to call at any time.
   */
  connectTo(destination: AudioNode): void {
    if (this.connectedTo === destination) return
    if (this.connectedTo) {
      try {
        this.volumeNode.disconnect(this.connectedTo)
      } catch {
        /* already disconnected */
      }
    }
    this.volumeNode.connect(destination)
    this.connectedTo = destination
  }

  /** Ramp to a new volume over a short constant so knob drags don't
   *  zipper. `when` defaults to `audioContext.currentTime`. */
  setVolume(v: number, when?: number): void {
    const target = Math.max(0, Math.min(1, v))
    const at = when ?? this.ctx.currentTime
    const g = this.volumeNode.gain
    g.cancelScheduledValues(at)
    g.setValueAtTime(g.value, at)
    g.linearRampToValueAtTime(target, at + 0.02)
  }

  /** -1..+1 pan, center 0. Same smooth-ramp treatment as volume. */
  setPan(p: number, when?: number): void {
    const target = Math.max(-1, Math.min(1, p))
    const at = when ?? this.ctx.currentTime
    const pan = this.panner.pan
    pan.cancelScheduledValues(at)
    pan.setValueAtTime(pan.value, at)
    pan.linearRampToValueAtTime(target, at + 0.02)
  }

  /** Snapshot current volume for UI readouts. */
  get volume(): number {
    return this.volumeNode.gain.value
  }

  get pan(): number {
    return this.panner.pan.value
  }

  /**
   * Look up a slot's `PluginHandle` by its store-side `BusEffect.id`.
   * Returns `null` for ids that don't resolve or for non-plugin
   * slots (the five built-in kinds don't expose a `PluginHandle`).
   *
   * Added for the SPAN-style FFT analyzer Plugin-tab UI: the
   * spectrum component needs to call `getAnalyzerId()` on the
   * FFT-analyzer's handle so it knows which `meters.getFftSnapshot`
   * to poll.
   */
  getPluginHandle(effectId: string): PluginHandle | null {
    for (const slot of this.slots) {
      if (slot.id === effectId) return slot.pluginHandle
    }
    return null
  }

  /**
   * Reconcile the live FX chain with the desired definitions. Diffs
   * by `id`:
   *   - Slots with the same id AND kind stay in place; params / wet
   *     / enabled are updated on the existing handle (no rebuild).
   *   - New ids get fresh slots built at the requested position.
   *   - Missing ids get disposed.
   *   - Reorder is handled by full rewire at the end so order
   *     changes don't require deep diff logic.
   *
   * Kind changes (same id, different kind) dispose + rebuild so we
   * don't try to morph a delay into a reverb.
   */
  setEffects(defs: BusEffect[]): void {
    const existing = new Map(this.slots.map(s => [s.id, s]))
    const keep = new Set<string>()
    const nextSlots: FxSlot[] = []
    for (const def of defs) {
      const prev = existing.get(def.id)
      if (prev && prev.kind === def.kind) {
        // Update in place.
        keep.add(def.id)
        this.applyEffectParams(prev, def)
        nextSlots.push(prev)
      } else {
        // Build fresh.
        const slot = this.buildSlot(def)
        this.applyEffectParams(slot, def)
        nextSlots.push(slot)
      }
    }
    // Dispose slots that didn't survive.
    for (const [id, slot] of existing) {
      if (!keep.has(id)) this.disposeSlot(slot)
    }
    this.slots = nextSlots
    this.rewireChain()
  }

  /** Disconnect from destination + clear internal chain. Called
   *  when a user bus is deleted. Master bus lives for the app's
   *  lifetime, so this rarely runs for master. */
  dispose(): void {
    if (this.connectedTo) {
      try {
        this.volumeNode.disconnect(this.connectedTo)
      } catch {
        /* already disconnected */
      }
      this.connectedTo = null
    }
    for (const slot of this.slots) this.disposeSlot(slot)
    this.slots = []
    try {
      this.inputNode.disconnect()
      this.panner.disconnect()
      this.chainHead.disconnect()
      this.chainTail.disconnect()
      this.volumeNode.disconnect()
    } catch {
      /* already clean */
    }
  }

  // ── FX chain internals ──────────────────────────────────────

  /** Disconnect + rebuild the serial chain from `this.slots`. Called
   *  whenever a slot is added, removed, or reordered. Safe to call
   *  on an empty chain (panner → volumeNode direct). */
  private rewireChain(): void {
    try {
      this.chainHead.disconnect()
    } catch {
      /* already disconnected */
    }
    for (const slot of this.slots) {
      try {
        slot.output.disconnect()
      } catch {
        /* already disconnected */
      }
    }
    if (this.slots.length === 0) {
      this.chainHead.connect(this.chainTail)
      return
    }
    let cursor: AudioNode = this.chainHead
    for (const slot of this.slots) {
      cursor.connect(slot.input)
      cursor = slot.output
    }
    cursor.connect(this.chainTail)
  }

  /** Build a fresh slot for the given definition. Wiring inside the
   *  slot is complete when this returns — chain hook-up happens in
   *  `rewireChain`. */
  private buildSlot(def: BusEffect): FxSlot {
    const input = this.ctx.createGain()
    const output = this.ctx.createGain()
    if (def.kind === 'filter') {
      // Serial — filter replaces the signal.
      const handle = buildMasterFilterFx(this.ctx)
      input.connect(handle.input)
      handle.output.connect(output)
      return {
        id: def.id,
        kind: 'filter',
        input,
        output,
        dryTap: null,
        wet: null,
        handle,
        pluginHandle: null,
        pluginId: null,
        userWet: DEFAULT_WET.filter,
        enabled: true,
        params: { ...DEFAULT_PARAMS.filter },
      }
    }
    if (def.kind === 'plugin') {
      // Registry-backed plugin. Look up by pluginId; fall back to a
      // no-op pass-through slot when the plugin isn't registered
      // (stale persisted id → no crash, just silence from the slot).
      const plugin = def.pluginId ? getEffect(def.pluginId) : undefined
      const dryTap = this.ctx.createGain()
      dryTap.gain.value = 1
      input.connect(dryTap).connect(output)
      let pluginHandle: PluginHandle | null = null
      let wet: GainNode | null = null
      if (plugin) {
        const preset = { ...plugin.defaultPreset, ...(def.params ?? {}) }
        pluginHandle = plugin.createNode(this.ctx, preset)
        wet = this.ctx.createGain()
        wet.gain.value = 0
        input.connect(pluginHandle.input)
        pluginHandle.output.connect(wet)
        wet.connect(output)
      }
      return {
        id: def.id,
        kind: 'plugin',
        input,
        output,
        dryTap,
        wet,
        handle: null,
        pluginHandle,
        pluginId: def.pluginId ?? null,
        userWet: DEFAULT_WET.plugin,
        enabled: true,
        params: { ...(def.params ?? {}) },
      }
    }
    // Parallel send — input splits into dry + effect; sum at output.
    const dryTap = this.ctx.createGain()
    dryTap.gain.value = 1
    input.connect(dryTap).connect(output)
    let handle: AnyFxHandle
    switch (def.kind) {
      case 'delay':
        handle = buildDelayFx(this.ctx)
        break
      case 'reverb':
        handle = buildReverbFx(this.ctx)
        break
      case 'bitcrush':
        handle = buildBitcrushFx(this.ctx)
        break
      case 'stutter':
        handle = buildStutterFx(this.ctx)
        break
      default:
        // Exhaustive — TypeScript should catch unseen kinds at
        // compile time, but fall back to a reverb so a mis-typed
        // stored def doesn't crash the engine.
        handle = buildReverbFx(this.ctx)
    }
    input.connect(handle.input)
    const wet = 'wet' in handle ? handle.wet : null
    if (wet) wet.connect(output)
    return {
      id: def.id,
      kind: def.kind,
      input,
      output,
      dryTap,
      wet,
      handle,
      pluginHandle: null,
      pluginId: null,
      userWet: DEFAULT_WET[def.kind],
      enabled: true,
      params: { ...DEFAULT_PARAMS[def.kind] },
    }
  }

  /** Apply enabled / wet / params from the definition to a slot's
   *  live AudioParams. No-op when every field already matches.
   *  Smoothly ramps wet so enable/disable toggles don't click. */
  private applyEffectParams(slot: FxSlot, def: BusEffect): void {
    const enabled = def.enabled !== false
    const userWet = typeof def.wet === 'number' ? def.wet : slot.userWet
    const params = def.params ?? {}
    slot.enabled = enabled
    slot.userWet = Math.max(0, Math.min(1, userWet))
    slot.params = { ...slot.params, ...params }
    this.applyWet(slot)
    this.applyKindParams(slot)
  }

  /** Ramp the slot's wet gain to reflect the user-set wet × enabled
   *  flag. For serial effects (filter) this becomes a bypass toggle
   *  by forcing the filter's frequency to transparent when disabled. */
  private applyWet(slot: FxSlot): void {
    const t = this.ctx.currentTime
    const ramp = 0.05
    const effective = slot.enabled ? slot.userWet : 0
    if (slot.wet) {
      slot.wet.gain.cancelScheduledValues(t)
      slot.wet.gain.setValueAtTime(slot.wet.gain.value, t)
      slot.wet.gain.linearRampToValueAtTime(effective, t + ramp)
    }
    if (slot.kind === 'filter') {
      const handle = slot.handle as MasterFilterFx
      // Filter type — honour user selection. Disabled / dry slot
      // flips to allpass so the signal is truly bypassed regardless
      // of mode (a lowpass at 22 kHz still has ~0.5 dB of ripple at
      // 10 kHz; allpass is surgically transparent).
      const mode = slot.params.filterMode ?? DEFAULT_PARAMS.filter.filterMode!
      const effectiveMode = slot.enabled && slot.userWet > 0 ? mode : 0
      if (effectiveMode === 0) handle.filter.type = 'allpass'
      else if (effectiveMode === 1) handle.filter.type = 'highpass'
      else handle.filter.type = 'lowpass'

      // Resonance (Q). Clamped — values above ~30 are unstable on
      // some browsers' BiquadFilter.
      const q = Math.max(0.01, Math.min(30, slot.params.q ?? 0.707))
      handle.filter.Q.cancelScheduledValues(t)
      handle.filter.Q.setValueAtTime(handle.filter.Q.value, t)
      handle.filter.Q.linearRampToValueAtTime(q, t + ramp)

      // Cutoff. Allpass sits at an arbitrary frequency — the curve
      // is flat regardless — but we still ramp the underlying value
      // so a subsequent mode flip picks up the user's setting.
      handle.filter.frequency.cancelScheduledValues(t)
      handle.filter.frequency.setValueAtTime(handle.filter.frequency.value, t)
      const cutoff = slot.params.cutoff ?? DEFAULT_PARAMS.filter.cutoff!
      handle.filter.frequency.linearRampToValueAtTime(cutoff, t + ramp)
    }
  }

  /** Kind-specific param application. Updates things that don't live
   *  on the `wet` fader — delay time, feedback, bitcrush bit depth,
   *  etc. Cheap: assignments to AudioParams are all that happens. */
  private applyKindParams(slot: FxSlot): void {
    const t = this.ctx.currentTime
    switch (slot.kind) {
      case 'delay': {
        const handle = slot.handle as DelayFx
        const time = slot.params.time
        const feedback = slot.params.feedback
        if (typeof time === 'number') {
          handle.delay.delayTime.setTargetAtTime(time, t, 0.01)
        }
        if (typeof feedback === 'number') {
          handle.feedback.gain.setTargetAtTime(
            Math.max(0, Math.min(0.95, feedback)),
            t,
            0.01,
          )
        }
        applyWetFilter(handle.filter, slot.params, t)
        break
      }
      case 'bitcrush': {
        const handle = slot.handle as BitcrushFx
        const bits = slot.params.bits
        if (typeof bits === 'number') {
          // Swap the shaper curve when bit depth changes. 4-bit
          // matches the master bank's default; dropping to 2 is
          // audibly brutal, 8 is subtle.
          handle.shaper.curve = slot.enabled
            ? bitcrushCurve(Math.max(1, Math.min(12, Math.floor(bits))))
            : identityCurve()
        } else {
          handle.shaper.curve = slot.enabled
            ? bitcrushCurve(4)
            : identityCurve()
        }
        break
      }
      case 'stutter': {
        const handle = slot.handle as StutterFx
        const time = slot.params.time
        const feedback = slot.params.feedback
        if (typeof time === 'number') {
          handle.delay.delayTime.setTargetAtTime(time, t, 0.01)
        }
        if (typeof feedback === 'number') {
          handle.feedback.gain.setTargetAtTime(
            Math.max(0, Math.min(0.99, feedback)),
            t,
            0.01,
          )
        }
        applyWetFilter(handle.filter, slot.params, t)
        break
      }
      case 'filter': {
        // Filter cutoff is handled inside `applyWet` because it
        // couples with enabled. Nothing else to do here.
        break
      }
      case 'reverb':
        // Reverb's wet is the only param for MVP — no kind-specific
        // updates needed. Future: IR length / decay.
        break
      case 'plugin': {
        // Registry-backed plugin. Forward every persisted param to
        // the plugin's `setParam`. Plugins own their numeric clamp /
        // ramp behaviour so we don't second-guess them here.
        if (!slot.pluginHandle) break
        for (const [name, value] of Object.entries(slot.params)) {
          if (typeof value !== 'number') continue
          try {
            slot.pluginHandle.setParam(name, value)
          } catch {
            /* plugin rejects unknown param — harmless */
          }
        }
        break
      }
    }
  }

  /** Tear a slot's sub-graph down. The surrounding chain is
   *  rewired by `rewireChain` separately; this just frees the
   *  per-slot AudioNodes so they don't linger. */
  private disposeSlot(slot: FxSlot): void {
    try {
      slot.input.disconnect()
    } catch {
      /* already disconnected */
    }
    try {
      slot.output.disconnect()
    } catch {
      /* already disconnected */
    }
    if (slot.dryTap) {
      try {
        slot.dryTap.disconnect()
      } catch {
        /* already disconnected */
      }
    }
    if (slot.wet) {
      try {
        slot.wet.disconnect()
      } catch {
        /* already disconnected */
      }
    }
    // Registry plugin handles own their own internal nodes via
    // their PluginHandle.dispose contract — call it and let them
    // clean up. Built-in handles (DelayFx / ReverbFx / ...) are
    // just disconnected; GC handles the rest once their references
    // drop out of scope.
    if (slot.pluginHandle) {
      try {
        slot.pluginHandle.dispose()
      } catch {
        /* plugin already disposed */
      }
    }
    if (slot.handle) {
      try {
        slot.handle.input.disconnect()
      } catch {
        /* already disconnected */
      }
      try {
        slot.handle.output.disconnect()
      } catch {
        /* already disconnected */
      }
    }
  }
}
